import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  AssaySchemaPermissionSettingsQuery,
  AssaySchemaPermissionSettingsQueryVariables,
} from 'results/__generated/AssaySchemaPermissionSettingsQuery';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

type AssaySchema = ExtractByTypename<AssaySchemaPermissionSettingsQuery['assaySchema'], 'RequestSchemaNode'>;

export const ASSAY_SCHEMA_PERMISSION_SETTINGS_QUERY = gql`
  query AssaySchemaPermissionSettingsQuery($assaySchemaId: StrictString!)
  @skipAudit
  @engComponent(name: RESULTS) {
    assaySchema(id: $assaySchemaId) {
      ... on PoliceableInterface {
        id
        permissions {
          definitionEditable
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryingProps {
  assaySchemaId: string;
}
function QueryingAssaySchemaPermissionSettings({assaySchemaId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    AssaySchemaPermissionSettingsQuery,
    AssaySchemaPermissionSettingsQueryVariables
  >(ASSAY_SCHEMA_PERMISSION_SETTINGS_QUERY, {
    variables: {assaySchemaId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.assaySchema), 'Expected assay schema to be accessible.');
    return (
      <ExistingAssaySchemaPermissionSettings
        assaySchema={result.data.assaySchema}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingAssaySchemaPermissionSettings({
  assaySchema,
  refetch,
}: {
  assaySchema: AssaySchema;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    assaySchema.collaboratorsJson
  );
  return (
    <SchemaWithOrgPermissionsSettings
      id={assaySchema.id}
      isReadOnly={!assaySchema.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await AssaySchemaActions.updateAssaySchemaCollaborators(assaySchema.id, {
          collaborators: minimalCollaborators,
        });
        await refetch();
      }}
    />
  );
}

export default QueryingAssaySchemaPermissionSettings;
